/**
 * @file useElementSize
 * @author Yu Zong(liuxuanzy@qq.com)
 */
import { useCallback, useRef } from 'react';
import { useRefFunction } from '@tencent/glue-react-use';
import { addResizeListener, removeResizeListener, supportResizeHandler, useElementMutation } from '@tencent/glue-react-mutation';
import { getDefaultElementSize, isElementSizeEqual } from '@tencent/glue-util-boundary';
import { getElementSize } from '@tencent/glue-dom-boundary';

function useElementSizeHandler(isEnable, onChange) {
  var sizeRef = useRef(getDefaultElementSize());
  var onChangeCallback = useRefFunction(onChange);
  var isEnableRef = useRefFunction(isEnable);
  var element = useRef(null);
  var update = useCallback(function (size) {
    var init = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

    if (!isEnableRef()) {
      return;
    }

    if (init || !isElementSizeEqual(sizeRef.current, size)) {
      sizeRef.current = size;
      onChangeCallback(size, init);
    }
  }, []);
  var ref = useCallback(function (node) {
    var el = element.current;

    if (el === node) {
      return;
    }

    element.current = node;

    if (el) {
      removeResizeListener(el, update);
    }

    if (node) {
      addResizeListener(node, update);
      update(getElementSize(node), true);
    }
  }, []);
  var callback = useCallback(function () {
    var el = element.current;

    if (!el) {
      return;
    }

    update(getElementSize(el));
  }, []);
  return [ref, callback];
}

function useElementSizeMutation(isEnable, onChange, options) {
  var sizeRef = useRef(getDefaultElementSize());
  var onChangeCallback = useRefFunction(onChange);
  return useElementMutation(isEnable, function (elem, init) {
    var size = getElementSize(elem);

    if (init || !isElementSizeEqual(sizeRef.current, size)) {
      sizeRef.current = size;
      onChangeCallback(size, init);
    }
  }, options);
}

export var useElementSize = supportResizeHandler() ? useElementSizeHandler : useElementSizeMutation;